import {
    Property,
    SsqSsqHeading,
    SsqSsqHeadingSsqProperty,
    SsqVersion,
} from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { Post, PostElement } from "src/api/client-api/post/postSchema";
import { formatUnitAbbreviation } from "src/utils/formatting";
import { isPropertyHidden } from "src/utils/ssq";
import { getSsqDisplayTitle } from "src/utils/ssqTitle";
import { buildExcelSheetName, buildExportFilename, ExportSubject } from "src/utils/exportNaming";

const exportHeading = (
    ssqSsqHeading: SsqSsqHeading,
    parentSsqSsqHeading: SsqSsqHeading | null,
    mappingPropsToValues: { [id: number]: PostElement[] },
    all_rows: string[][],
    hiddenElements: string[],
) => {
    if (isConditionallyHidden("heading", ssqSsqHeading.id, hiddenElements)) return;

    ssqSsqHeading.children.forEach((childSsqSsqHeading: SsqSsqHeading) => {
        exportHeading(childSsqSsqHeading, ssqSsqHeading, mappingPropsToValues, all_rows, hiddenElements);
    });
    ssqSsqHeading.ssqSsqHeadingSsqProperties
        .map((ssqSsqHeadingSsqProperty: SsqSsqHeadingSsqProperty) => ssqSsqHeadingSsqProperty.ssqProperty)
        .forEach((property: Property, i: number) => {
            exportProperty(
                property,
                ssqSsqHeading,
                parentSsqSsqHeading,
                null,
                i,
                mappingPropsToValues,
                all_rows,
                hiddenElements,
            );
        });
};

const isConditionallyHidden = (entity: string, id: number, hiddenElements: string[]): boolean => {
    const element = `${entity}-${id}`;

    return hiddenElements.includes(element);
};

const formatReponse = (property: Property, element?: PostElement): string => {
    if (!element) return "";
    if (["select", "checkbox", "radio"].includes(property.field?.type ?? "") && property.dataList?.options.length) {
        return (
            element.dataListOptionCodes
                ?.map((answer) => property.dataList?.options.find((option) => option.code === answer)?.label)
                .join(", ") ?? ""
        );
    }
    return element.value;
};

const formatReponseGroup = (i: number, element?: PostElement) => {
    return i + 1 + "" + (element?.position !== undefined ? 1 + element.position : "");
};

const formatComments = (element?: PostElement): string => {
    if (!element?.ssqPostComments || element.ssqPostComments.length === 0) {
        return "";
    }

    return element.ssqPostComments
        .map((comment) => {
            const author = `${comment.createdBy.firstname || ""} ${comment.createdBy.lastname || ""}`.trim();
            const date = new Date(comment.updatedAt).toLocaleDateString("fr-FR");
            return `${author} (${date}): ${comment.comment}`;
        })
        .join(" | ");
};

export const hasAttachment = (element?: PostElement): string => {
    return element?.ssqPostAttachments && element.ssqPostAttachments.length > 0 ? "O" : "N";
};

const exportProperty = (
    property: Property,
    ssqSsqHeading: SsqSsqHeading,
    parentSsqSsqHeading: SsqSsqHeading | null,
    parentProperty: Property | null,
    i: number,
    mappingPropsToValues: { [id: number]: PostElement[] },
    all_rows: string[][],
    hiddenElements: string[],
) => {
    if (isPropertyHidden(property.id, hiddenElements, ssqSsqHeading.ssqVersionStructureId)) return;

    if (property.children.length !== 0) {
        property.children.forEach((child: Property) => {
            exportProperty(
                child,
                ssqSsqHeading,
                parentSsqSsqHeading,
                property,
                i,
                mappingPropsToValues,
                all_rows,
                hiddenElements,
            );
        });
    } else if (!property.hidden) {
        const emptyValue = "";
        const rowGenerator = (element?: PostElement) => {
            return [
                parentSsqSsqHeading !== null ? parentSsqSsqHeading.ssqHeading.title : ssqSsqHeading.ssqHeading.title,
                parentSsqSsqHeading !== null ? ssqSsqHeading.ssqHeading.title : emptyValue,
                parentProperty !== null ? parentProperty.title : emptyValue,
                property.title,
                parentProperty !== null ? formatReponseGroup(i, element) : emptyValue,
                element !== null ? formatReponse(property, element) : emptyValue,
                formatUnitAbbreviation(property.unit),
                property.field?.type ?? "",
                hasAttachment(element),
                formatComments(element),
            ];
        };

        const propertyElements = mappingPropsToValues[property.id] ?? [];
        const occurrenceElements = propertyElements.filter(
            (element) => element.ssqVersionStructureId === ssqSsqHeading.ssqVersionStructureId,
        );
        const elementsToExport =
            occurrenceElements.length > 0 ?
                occurrenceElements
            :   propertyElements.filter((element) => element.ssqVersionStructureId === undefined);

        if (elementsToExport.length > 0) {
            elementsToExport.forEach((e) => {
                all_rows.push(rowGenerator(e));
            });
        } else {
            all_rows.push(rowGenerator());
        }
    }
};

export const exportSsqToExcel = async (
    headers: string[],
    ssqVersion: SsqVersion,
    post: Post,
    subject: ExportSubject,
) => {
    const XLSX = await import("xlsx");

    const workbook = XLSX.utils.book_new();
    const all_rows: string[][] = [];
    all_rows.push(headers);
    ssqVersion.parentSsqSsqHeadings.forEach((ssqSsqHeading: SsqSsqHeading) => {
        const mappingPropsToValues: { [id: number]: PostElement[] } = {};
        post.elements?.forEach((element) => {
            if (!(element.ssqPropertyId in mappingPropsToValues)) {
                mappingPropsToValues[element.ssqPropertyId] = [];
            }
            mappingPropsToValues[element.ssqPropertyId].push(element);
        });
        exportHeading(ssqSsqHeading, null, mappingPropsToValues, all_rows, post.hiddenElements);
    });
    const worksheet = XLSX.utils.aoa_to_sheet(all_rows);

    const questionnaireTitle = getSsqDisplayTitle(ssqVersion.ssq);
    const filename = buildExportFilename({
        subject,
        content: questionnaireTitle,
        scope: post.campaign?.label,
        extension: "xlsx",
    });

    XLSX.utils.book_append_sheet(workbook, worksheet, buildExcelSheetName(questionnaireTitle));
    XLSX.writeFile(workbook, filename);
};
